import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { apiFetch, BACKEND_URL } from '@/lib/api';
import { patchBusinessProfile, uploadBusinessLogo, deleteBusinessLogo } from '@/lib/events-api';
import { ArrowLeft, Trash2, Star, CreditCard, Gift, Calendar, Camera, Briefcase, CheckCircle, Circle, Loader2, Upload, X, Globe } from 'lucide-react';
import { cancelProSubscription } from '@/lib/pro-api';

const ACCOUNT_TYPE_OPTIONS = [
  { key: 'HOST', title: 'Host', hint: 'I create events and collect memories', Icon: Calendar },
  { key: 'ATTENDEE', title: 'Attendee', hint: 'I attend events & share my photos', Icon: Camera },
  { key: 'PROFESSIONAL', title: 'Professional / Business', hint: 'I host events for clients (DJ, planner, venue\u2026)', Icon: Briefcase },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, refreshUser, signOut } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [proSnap, setProSnap] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [acctBusy, setAcctBusy] = useState(null); // null | 'HOST' | 'ATTENDEE' | 'PROFESSIONAL'
  // Phase 13b — business profile state
  const [bizDisplayName, setBizDisplayName] = useState('');
  const [bizWebsite, setBizWebsite] = useState('');
  const [bizSaving, setBizSaving] = useState(false);
  const [bizLogoUploading, setBizLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoadingSessions(true); setError(null);
    try {
      const s = await apiFetch('/api/v1/auth/sessions');
      setSessions(s.sessions || []);
    } catch (e) {
      if (e.status === 401) { await signOut(); return; }
      setError(e.message);
    } finally { setLoadingSessions(false); }
  }, [signOut]);

  useEffect(() => { load(); }, [load]);

  // Sync business profile fields when user loads
  useEffect(() => {
    if (user) {
      setBizDisplayName(user.business_display_name || '');
      setBizWebsite(user.business_website || '');
    }
  }, [user]);

  async function revoke(id) {
    try { await apiFetch(`/api/v1/auth/sessions/${id}`, { method: 'DELETE' }); await load(); }
    catch (e) { setError(e.message); }
  }

  async function requestDeletion() {
    setConfirmDelete(false);
    try { await apiFetch('/api/v1/users/me/deletion-request', { method: 'POST' }); await signOut(false); navigate('/'); }
    catch (e) { setError(e.message); }
  }

  async function onCancelPro() {
    if (!window.confirm('Cancel your PRO subscription? It will stay active until the end of the billing period.')) return;
    setCancelling(true);
    try { const r = await cancelProSubscription(); setProSnap(r.subscription); }
    catch (e) { setError(e?.message || 'Cancel failed.'); }
    finally { setCancelling(false); }
  }

  async function changeAccountType(next) {
    if (acctBusy) return;
    const current = (user?.account_type || 'ATTENDEE').toUpperCase();
    if (current === next) return;
    // Upgrading to PROFESSIONAL requires business fields
    if (next === 'PROFESSIONAL' && !user?.business_name) {
      if (window.confirm('To switch to a Professional account, add your business details on the Premium screen.\n\nContinue?')) {
        navigate('/host-premium');
      }
      return;
    }
    setAcctBusy(next);
    setError(null);
    try {
      const body = { account_type: next };
      if (next === 'PROFESSIONAL') {
        body.business_name = user?.business_name;
        body.business_category = user?.business_category;
      }
      await apiFetch('/api/v1/auth/users/me/account-type', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      await refreshUser();
    } catch (e) {
      setError(e?.message || 'Could not update account type.');
    } finally { setAcctBusy(null); }
  }

  const currentType = (user?.account_type || 'ATTENDEE').toUpperCase();
  const isProfessional = currentType === 'PROFESSIONAL';

  async function saveBusinessProfile() {
    setBizSaving(true); setError(null);
    try {
      await patchBusinessProfile({ business_display_name: bizDisplayName.trim(), business_website: bizWebsite.trim() });
      await refreshUser();
    } catch (e) { setError(e?.message || 'Could not save business profile.'); }
    finally { setBizSaving(false); }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBizLogoUploading(true); setError(null);
    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await uploadBusinessLogo(base64);
      await refreshUser();
    } catch (err) { setError(err?.message || 'Logo upload failed.'); }
    finally { setBizLogoUploading(false); if (logoInputRef.current) logoInputRef.current.value = ''; }
  }

  async function handleLogoRemove() {
    if (!window.confirm('Remove your business logo?')) return;
    setBizLogoUploading(true); setError(null);
    try { await deleteBusinessLogo(); await refreshUser(); }
    catch (err) { setError(err?.message || 'Could not remove logo.'); }
    finally { setBizLogoUploading(false); }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="settings-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> back
      </button>
      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-4" data-testid="screen-title">Profile &amp; Settings</h1>

      {/* Profile card */}
      <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="profile-card">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#F3F5F8] flex items-center justify-center overflow-hidden flex-shrink-0">
            {user?.profile_photo_url ? (
              <img src={`${BACKEND_URL}${user.profile_photo_url}`} alt="" className="w-16 h-16 object-cover" data-testid="profile-photo" />
            ) : (
              <span className="text-2xl font-extrabold text-[#0B1B46]">{(user?.first_name?.[0] || '?').toUpperCase()}</span>
            )}
          </div>
          <div>
            <p className="text-lg font-bold text-[#0B1B46]" data-testid="profile-name">{user?.first_name} {user?.last_name}</p>
            <p className="text-sm text-[#5B6472]" data-testid="profile-email">{user?.email}</p>
            <p className="text-sm text-[#5B6472]" data-testid="profile-phone">{user?.phone}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${user?.email_verified ? 'bg-[#E4F4EC] text-[#1F8A55]' : 'bg-[#FBE5E5] text-[#C93434]'}`} data-testid="settings-badge-email">
            {user?.email_verified ? 'Email verified' : 'Email not verified'}
          </span>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${user?.phone_verified ? 'bg-[#E4F4EC] text-[#1F8A55]' : 'bg-[#FBE5E5] text-[#C93434]'}`} data-testid="settings-badge-phone">
            {user?.phone_verified ? 'Phone verified' : 'Phone not verified'}
          </span>
        </div>
      </div>

      {/* Phase 12a — Account-type switcher card */}
      <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="settings-account-type-card">
        <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-1">How will you use ALLVIEWS?</p>
        <p className="text-xs text-[#5B6472] mb-3">This tailors what you see first. You can still host or attend events regardless.</p>
        <div className="flex flex-col gap-2">
          {ACCOUNT_TYPE_OPTIONS.map(({ key, title, hint, Icon }) => {
            const active = currentType === key;
            const busy = acctBusy === key;
            return (
              <button key={key} type="button" onClick={() => changeAccountType(key)} disabled={busy || active}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${active ? 'border-[#146BFF] bg-[#EFF5FF]' : 'border-[#E3E8EF] bg-white hover:border-[#B8CFFF]'} disabled:cursor-default`}
                data-testid={`settings-account-type-${key.toLowerCase()}`}>
                <div className={`w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 border transition-colors ${active ? 'bg-[#146BFF] border-[#146BFF]' : 'bg-[#F3F5F8] border-[#E3E8EF]'}`}>
                  <Icon className={`w-[18px] h-[18px] ${active ? 'text-white' : 'text-[#146BFF]'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-extrabold ${active ? 'text-[#146BFF]' : 'text-[#0B1B46]'}`}>{title}</p>
                  <p className="text-xs text-[#5B6472] leading-tight">{hint}</p>
                </div>
                {busy ? (
                  <Loader2 className="w-5 h-5 text-[#146BFF] flex-shrink-0 animate-spin" />
                ) : active ? (
                  <CheckCircle className="w-5 h-5 text-[#146BFF] flex-shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-[#C6CDD5] flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Phase 13b — Business profile card (PROFESSIONAL only) */}
      {isProfessional && (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="settings-business-card">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase className="w-4 h-4 text-[#146BFF]" />
            <span className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Business profile</span>
          </div>

          {/* Logo */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-14 h-14 rounded-xl bg-[#F3F5F8] border border-[#E3E8EF] flex items-center justify-center overflow-hidden flex-shrink-0">
              {user?.business_logo_url ? (
                <img src={`${BACKEND_URL}${user.business_logo_url}`} alt="" className="w-14 h-14 object-contain" data-testid="settings-business-logo-img" />
              ) : (
                <Briefcase className="w-6 h-6 text-[#C6CDD5]" />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <input type="file" accept="image/png,image/jpeg,image/webp" ref={logoInputRef} onChange={handleLogoUpload} className="hidden" data-testid="settings-business-logo-input" />
              <button onClick={() => logoInputRef.current?.click()} disabled={bizLogoUploading}
                className="h-8 px-3 bg-[#EFF5FF] text-[#146BFF] text-xs font-semibold rounded-lg flex items-center gap-1 hover:bg-[#D6E4FF] disabled:opacity-50 transition-colors" data-testid="settings-business-logo-upload-btn">
                <Upload className="w-3 h-3" /> {bizLogoUploading ? 'Uploading\u2026' : user?.business_logo_url ? 'Replace logo' : 'Upload logo'}
              </button>
              {user?.business_logo_url && (
                <button onClick={handleLogoRemove} disabled={bizLogoUploading}
                  className="h-8 px-3 bg-white border border-[#E3E8EF] text-[#5B6472] text-xs font-semibold rounded-lg flex items-center gap-1 hover:bg-[#F3F5F8] disabled:opacity-50 transition-colors" data-testid="settings-business-logo-remove-btn">
                  <X className="w-3 h-3" /> Remove
                </button>
              )}
            </div>
          </div>

          {/* Display name */}
          <div className="mb-3">
            <label htmlFor="biz-display" className="block text-xs font-semibold text-[#0B1B46] mb-1">Display name</label>
            <input id="biz-display" value={bizDisplayName} onChange={e => setBizDisplayName(e.target.value)} placeholder="e.g. Sarah Foto Co."
              className="w-full h-10 px-3 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid="settings-business-display-name" />
          </div>

          {/* Website */}
          <div className="mb-3">
            <label htmlFor="biz-web" className="block text-xs font-semibold text-[#0B1B46] mb-1">Website</label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
              <input id="biz-web" value={bizWebsite} onChange={e => setBizWebsite(e.target.value)} placeholder="https://example.com"
                className="w-full h-10 pl-9 pr-3 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid="settings-business-website" />
            </div>
          </div>

          <button onClick={saveBusinessProfile} disabled={bizSaving}
            className="h-10 w-full bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg text-sm transition-colors" data-testid="settings-business-save-btn">
            {bizSaving ? 'Saving\u2026' : 'Save business profile'}
          </button>
        </div>
      )}

      {/* PRO subscription card */}
      <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="settings-pro-card">
        <div className="flex items-center gap-2 mb-2">
          <Star className="w-4 h-4 text-[#146BFF]" />
          <span className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">ALLVIEWS PRO</span>
        </div>
        {proSnap?.active ? (
          <>
            <p className="text-sm text-[#0B1B46]"><span className="font-bold">{proSnap.plan === 'ANNUAL' ? 'Annual' : 'Monthly'}</span> &middot; renews {proSnap.current_period_end ? new Date(proSnap.current_period_end).toLocaleDateString() : 'N/A'}</p>
            <div className="flex items-center gap-1 mt-1"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${proSnap.cancel_at_period_end ? 'bg-[#FFF6E5] text-[#B86500]' : 'bg-[#E4F4EC] text-[#1F8A55]'}`} data-testid="settings-pro-status">{proSnap.cancel_at_period_end ? 'Cancels at period end' : 'Active'}</span></div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => navigate('/pro-referrals')} className="h-9 px-4 bg-[#EFF5FF] text-[#146BFF] font-semibold rounded-lg text-sm flex items-center gap-1 hover:bg-[#D6E4FF] transition-colors" data-testid="settings-pro-referrals-btn"><Gift className="w-3.5 h-3.5" /> Referral earnings</button>
              {!proSnap.cancel_at_period_end && (
                <button onClick={onCancelPro} disabled={cancelling} className="h-9 px-4 bg-white border border-[#E3E8EF] text-[#5B6472] font-semibold rounded-lg text-sm hover:bg-[#F3F5F8] transition-colors disabled:opacity-50" data-testid="settings-pro-cancel-btn">{cancelling ? 'Cancelling\u2026' : 'Cancel'}</button>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-[#5B6472]">Every event premium, unlimited uploads, referral earnings.</p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => navigate('/host-premium')} className="h-9 px-4 bg-[#146BFF] text-white font-semibold rounded-lg text-sm flex items-center gap-1 hover:bg-[#0F55CC] transition-colors" data-testid="settings-pro-upgrade-btn"><CreditCard className="w-3.5 h-3.5" /> See plans</button>
              <button onClick={() => navigate('/pro-referrals')} className="h-9 px-4 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg text-sm flex items-center gap-1 hover:bg-[#F3F5F8] transition-colors" data-testid="settings-pro-referrals-btn"><Gift className="w-3.5 h-3.5" /> Referrals</button>
            </div>
          </>
        )}
      </div>

      {/* Phase 15 — Team & Transfer inbox links (always visible if logged in) */}
      <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="settings-inbox-card">
        <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Inbox</p>
        <button onClick={() => navigate('/transfer-inbox')} className="w-full flex items-center justify-between py-2.5 text-sm font-semibold text-[#0B1B46] hover:text-[#146BFF] transition-colors" data-testid="settings-transfer-inbox-btn">
          Incoming event transfers <span className="text-[#5B6472]">&rsaquo;</span>
        </button>
        <button onClick={() => navigate('/team-invites')} className="w-full flex items-center justify-between py-2.5 text-sm font-semibold text-[#0B1B46] hover:text-[#146BFF] transition-colors border-t border-[#E3E8EF]" data-testid="settings-team-invites-btn">
          Incoming team invites <span className="text-[#5B6472]">&rsaquo;</span>
        </button>
      </div>

      {/* SuperAdmin tools (SUPER_ADMIN only) */}
      {user?.role === 'SUPER_ADMIN' && (
        <div className="bg-white border border-red-200 rounded-xl p-4 mb-4" data-testid="settings-admin-card">
          <p className="text-[10px] font-extrabold text-red-500 tracking-widest uppercase mb-2">SuperAdmin</p>
          <button onClick={() => navigate('/admin/suppressions')} className="w-full flex items-center justify-between py-2.5 text-sm font-semibold text-[#0B1B46] hover:text-[#146BFF] transition-colors" data-testid="settings-admin-suppressions-btn">
            Email Suppressions <span className="text-[#5B6472]">&rsaquo;</span>
          </button>
        </div>
      )}

      {/* Sessions */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Active sessions</span>
        <button onClick={load} className="text-[#146BFF] text-xs font-bold hover:underline" data-testid="sessions-refresh-btn">refresh</button>
      </div>
      {loadingSessions && <div className="flex justify-center py-4"><div className="w-6 h-6 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>}
      <div className="flex flex-col gap-2 mb-4">
        {sessions.map(s => (
          <div key={s.id} className="bg-white border border-[#E3E8EF] rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`session-${s.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-bold text-[#0B1B46]" data-testid={`session-device-${s.id}`}>
                {s.device_name} {s.is_current ? '· This device' : ''}
              </p>
              <p className="text-[11px] text-[#5B6472] font-mono truncate">UA: {s.user_agent?.slice(0, 60)}</p>
              <p className="text-[11px] text-[#5B6472] font-mono">IP: {s.ip}</p>
              <p className="text-[11px] text-[#5B6472]">Last active: {new Date(s.last_active_at).toLocaleString()}</p>
            </div>
            {s.is_current ? (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#EFF5FF] text-[#146BFF] whitespace-nowrap" data-testid={`session-current-${s.id}`}>Current</span>
            ) : (
              <button onClick={() => revoke(s.id)} className="px-3 py-1.5 bg-white border border-[#E3E8EF] text-sm font-semibold rounded-lg hover:bg-[#F3F5F8] whitespace-nowrap" data-testid={`session-revoke-${s.id}`}>Revoke</button>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2 mb-6">
        <button onClick={async () => { await signOut(false); navigate('/'); }}
          className="h-10 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
          data-testid="settings-sign-out-btn">Sign out this device</button>
        <button onClick={async () => { try { await apiFetch('/api/v1/auth/sign-out-all', { method: 'POST', skipRefresh: true }); } catch (_e) { /* ignore */ } await signOut(false); navigate('/'); }}
          className="h-10 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
          data-testid="settings-sign-out-all-btn">Sign out of all devices</button>
      </div>

      {/* Account deletion */}
      <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-xl p-4 mb-4" data-testid="deletion-card">
        <p className="font-bold text-[#0B1B46] mb-1">Delete account</p>
        <p className="text-sm text-[#5B6472] mb-3">Requesting deletion signs you out everywhere and marks your account for permanent removal.</p>
        <button onClick={() => setConfirmDelete(true)}
          className="h-10 w-full bg-[#C93434] hover:bg-[#a02a2a] text-white font-semibold rounded-lg transition-colors"
          data-testid="settings-delete-btn">Request account deletion</button>
      </div>

      {error && <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434] mb-4" data-testid="settings-error">{error}</div>}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-[#0B1B46]/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <p className="font-bold text-[#0B1B46] mb-1">Are you absolutely sure?</p>
            <p className="text-sm text-[#5B6472] mb-4">Your sessions will be revoked immediately.</p>
            <button onClick={requestDeletion} className="w-full h-10 bg-[#C93434] text-white font-semibold rounded-lg mb-2" data-testid="settings-delete-confirm-btn">Yes, request deletion</button>
            <button onClick={() => setConfirmDelete(false)} className="w-full h-10 text-[#5B6472] font-semibold rounded-lg hover:bg-[#F3F5F8]" data-testid="settings-delete-cancel-btn">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
